import React, { useCallback, useEffect, useState, useRef } from "react";
import Layout4 from "../../DataLayouts/Layout4";
import NextWeekIcon from "@mui/icons-material/NextWeek";
import FormatAlignJustifyIcon from "@mui/icons-material/FormatAlignJustify";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import { MAIN_URL } from "../../../Configurations/Urls";
import toast from "react-hot-toast";
import TableDataGeneric from "../../../Configurations/TableDataGeneric";
import { useNavigate, useSearchParams } from "react-router-dom";
import { operatorRequiresValue } from "../../../Components/filters/operators";
import { Box, Typography } from "@mui/material";
import InboxIcon from "@mui/icons-material/Inbox";

const DEFAULT_COLUMNS = [
  {
    field: "work_model_name",
    label: "Work Model Name",
    visible: true,
    width: 200,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: true,
  },
  {
    field: "working_days_display",
    label: "Working Days",
    visible: true,
    width: 180,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  
  {
    field: "remarks",
    label: "Remarks",
    visible: true,
    width: 250,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
];

const DEFAULT_PAGINATION = {
  page: 1,
  pageSize: 10,
  total: 0,
  totalPages: 1,
  hasNext: false,
  hasPrev: false,
};

const DEFAULT_URL_PARAMS = {
  page: "1",
  pageSize: "10",
  sortBy: "organization_work_model_id",
  sortOrder: "desc",
};

// Helper functions
const buildFiltersFromParams = (searchParams) => {
  const filters = [];
  const processedFields = new Set();

  searchParams.forEach((value, key) => {
    if (
      key.endsWith("_operator") ||
      key.endsWith("_logic") ||
      !key.startsWith("filter_")
    )
      return;

    const field = key.replace("filter_", "");
    if (processedFields.has(field)) return;
    processedFields.add(field);

    const operator = searchParams.get(`filter_${field}_operator`) || "contains";
    const requiresValue = operatorRequiresValue(operator);

    if (!requiresValue || value) {
      let filterValue = requiresValue ? value : null;

      if (requiresValue && filterValue) {
        if (["equals", "not_equals"].includes(operator)) {
          filterValue = filterValue
            .split(",")
            .map((v) => v.trim().toLowerCase());
        } else if (["between", "not_between"].includes(operator)) {
          filterValue = filterValue.split(",").map((v) => v.trim());
        }
      }

      filters.push({ field, operator, value: filterValue });
    }
  });

  return filters;
};

function WorkModelDayList() {
  const [workModelDays, setWorkModelDays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tableConfig, setTableConfig] = useState(null);
  const [configColumns, setConfigColumns] = useState(DEFAULT_COLUMNS);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [paginationData, setPaginationData] = useState(DEFAULT_PAGINATION);

  const { userData } = useAuthStore();
  const org = userData?.organization;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const fetchControllerRef = useRef(null);
  const hasInitializedURL = useRef(false);

  // Initialize URL parameters
  useEffect(() => {
    if (hasInitializedURL.current) return;

    const params = new URLSearchParams(searchParams);
    let needsUpdate = false;

    Object.entries(DEFAULT_URL_PARAMS).forEach(([key, defaultValue]) => {
      if (!params.has(key)) {
        params.set(key, defaultValue);
        needsUpdate = true;
      }
    });

    if (needsUpdate) {
      setSearchParams(params, { replace: true });
    }

    hasInitializedURL.current = true;
  }, [searchParams, setSearchParams]);

  // Load table configuration from general-datagrids API
  useEffect(() => {
    const loadTableConfiguration = async () => {
      if (!org?.organization_id) {
        setLoadingConfig(false);
        return;
      }

      try {
        const configRes = await fetch(`${MAIN_URL}/api/general-datagrids`, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        });

        if (configRes.ok) {
          const configResponse = await configRes.json();
          const datagrids = configResponse.datagrids || [];
          const orgKey = `WorkModelDays_grid_${org.organization_id}`;
          const savedConfig = datagrids.find(
            (dg) => dg.datagrid_key === orgKey
          );

          if (savedConfig) {
            const serverCfg = savedConfig.datagrid_default_configuration;
            setTableConfig(serverCfg);

            if (
              serverCfg?.columns &&
              Array.isArray(serverCfg.columns) &&
              serverCfg.columns?.length > 0
            ) {
              setConfigColumns(serverCfg.columns);
            } else {
              setConfigColumns(DEFAULT_COLUMNS);
            }
          } else {
            setConfigColumns(DEFAULT_COLUMNS);
          }
        }
      } catch (error) {
        console.error("Error loading table configuration:", error);
        setConfigColumns(DEFAULT_COLUMNS);
      } finally {
        setLoadingConfig(false);
      }
    };

    loadTableConfiguration();
  }, [org?.organization_id]);

  // Load work model days data
  useEffect(() => {
    if (!org?.organization_id || !hasInitializedURL.current) return;

    fetchControllerRef.current?.abort();
    fetchControllerRef.current = new AbortController();

    setLoading(true);

    const fetchData = async () => {
      try {
        const filters = buildFiltersFromParams(searchParams);
        const search = searchParams.get("search");
        const requestBody = {
          filters,
          page: Number.parseInt(searchParams.get("page"), 10) || 1,
          pageSize: Number.parseInt(searchParams.get("pageSize"), 10) || 10,
          sort: {
            field: searchParams.get("sortBy") || "organization_work_model_id",
            direction: searchParams.get("sortOrder") || "desc",
          },
          ...(search && search.trim() !== "" && { search: search.trim() }),
        };

        // Try new POST endpoint first, fallback to old GET endpoint if needed
        const response = await fetch(
          `${MAIN_URL}/api/organizations/${org.organization_id}/workmodel-days-filtered`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(requestBody),
            signal: fetchControllerRef.current.signal,
          }
        );

        let data;
        if (response.ok) {
          const result = await response.json();
          if (result.success && result.data) {
            data = result.data;
            
            if (result.pagination) {
              const pageSize =
                result.pagination.pageSize || result.pagination.limit || 10;
              setPaginationData({
                page:
                  result.pagination.page ||
                  Number.parseInt(searchParams.get("page"), 10) ||
                  1,
                pageSize,
                total: result.pagination.total || 0,
                totalPages: result.pagination.totalPages || 1,
                hasNext: result.pagination.hasNext || false,
                hasPrev: result.pagination.hasPrev || false,
              });
            }
          } else {
            data = [];
            setPaginationData(DEFAULT_PAGINATION);
          }
        } else if (response.status === 404) {
          // Fallback to old GET endpoint if POST endpoint doesn't exist yet
          const fallbackResponse = await fetch(
            `${MAIN_URL}/api/organizations/${org.organization_id}/workmodel-days`,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
              },
            }
          );
          if (fallbackResponse.ok) {
            const fallbackData = await fallbackResponse.json();
            data = fallbackData?.workshiftDay?.data || [];
            setPaginationData(DEFAULT_PAGINATION);
          } else {
            data = [];
            setPaginationData(DEFAULT_PAGINATION);
          }
        } else {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        // Map the grouped data - preserve existing transformation logic
        const formattedData = data.map((item) => ({
          id: item.organization_work_model_id, // Used for edit/delete operations
          organization_work_model_id: item.organization_work_model_id,
          work_model_name: item.work_model_name || "N/A",
          working_days_display: item.working_days_display || "0 ()",
          working_days_count: item.working_days_count || 0,
          working_days_abbreviations: item.working_days_abbreviations || "",
          remarks: item.remarks || "-",
          days_detail: item.days_detail || [],
        }));

        setWorkModelDays(formattedData);
      } catch (err) {
        if (err.name === "AbortError") return;
        
        console.error("Error fetching work model days:", err);
        
        if (err.response?.status === 401) {
          toast.error("Session Expired!");
          window.location.href = "/login";
        } else {
          toast.error(
            err.response?.data?.error || "Failed to fetch work model days"
          );
          setWorkModelDays([]);
          setPaginationData(DEFAULT_PAGINATION);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [org?.organization_id, searchParams]);

  // Handle sort change
  const handleSortChange = useCallback(
    (field, direction) => {
      const params = new URLSearchParams(searchParams);
      params.set("sortBy", field);
      params.set("sortOrder", direction);
      params.set("page", "1"); // Reset to first page on sort
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  // Delete function
  const handleDelete = async (id) => {
    try {
      const response = await fetch(
        `${MAIN_URL}/api/organizations/${org?.organization_id}/workmodel-days/${id}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 200) {
        const result = await response.json();
        toast.success(
          result.message || "Work model days deleted successfully"
        );
        
        // Refresh the list after deletion
        const params = new URLSearchParams(searchParams);
        setSearchParams(params, { replace: true });
        
        return Promise.resolve();
      }
    } catch (error) {
      console.error("Delete failed:", error);
      
      if (error.response?.status === 401) {
        toast.error("Session Expired!");
        window.location.href = "/login";
      } else if (error.response?.status === 409) {
        toast.error(
          error.response.data.error ||
            "Cannot delete - linked with other records"
        );
      } else {
        toast.error(
          error.response?.data?.error || "Failed to delete work model days"
        );
      }
      
      return Promise.reject(error);
    }
  };

  // Edit function
  const handleEdit = useCallback(
    (item) => {
      navigate(
        `/organization/work-model-days/edit/${item.organization_work_model_id}`
      );
    },
    [navigate]
  );

  // Empty state component
  const EmptyState = () => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        py: 8,
        px: 2,
      }}
    >
      <InboxIcon sx={{ fontSize: 80, color: "grey.400", mb: 2 }} />
      <Typography variant="h6" color="text.secondary" gutterBottom>
        No Work Model Days Found
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {searchParams.toString().includes("filter_")
          ? "Try adjusting your filters to see more results."
          : "Get started by adding your first work model days."}
      </Typography>
    </Box>
  );

  return (
    <>
      <Layout4
        loading={loading}
        heading={"Work Model Days"}
        btnName={"Add Work Model Days"}
        Data={workModelDays}
        add_action={"SHIFT_MODEL_DAYS_ADD"}
        delete_action={"SHIFT_MODEL_DAYS_DELETE"}
        tableHeaders={[
          { name: "Work Model Name", value_key: "work_model_name" },
          { name: "Working Days", value_key: "working_days_display" },
          { name: "Days Count", value_key: "working_days_count" },
          { name: "Remarks", value_key: "remarks" },
        ]}
        Icons={[
          <FormatAlignJustifyIcon
            sx={{ fontSize: 60, color: "grey.500", mb: 2 }}
          />,
          <FormatAlignJustifyIcon color="primary" />,
          <NextWeekIcon sx={{ color: "text.secondary" }} />,
        ]}
        messages={[
          "Work Model Days",
          "Work Model Days",
          "Add Work Model Days",
          "Work Model Days",
        ]}
        Route={"/organization/work-model-days"}
        setData={setWorkModelDays}
        DeleteFunc={handleDelete}
      />

      {!loadingConfig && 
        <TableDataGeneric
          tableName="organization_work_model_days"
          primaryKey="organization_work_model_id"
          heading="Work Model Days"
          data={workModelDays}
          sortname={"work_model_name"}
          showActions={true}
          Route="/organization/work-model-days"
          DeleteFunc={handleDelete}
          EditFunc={handleEdit}
          edit_delete_action={["SHIFT_MODEL_DAYS_DELETE", "SHIFT_MODEL_DAYS_EDIT"]}
          token={localStorage.getItem("token")}
          configss={configColumns}
          {...(tableConfig && { config: tableConfig })}
          paginationData={paginationData}
          onSortChange={handleSortChange}
          filterModule="organization_work_model_days"
        />
      }
    </>
  );
}

export default WorkModelDayList;
